import { getPlatformFeePercent, getPlatformFeeRate } from "./liberFees";

export function isReviewNotification(notif) {
  const kind = String(notif?.metadata?.kind ?? "").toLowerCase();
  const type = String(notif?.type ?? "").toLowerCase();
  return (
    kind === "review_received" ||
    kind === "review_submitted" ||
    kind === "review_prompt" ||
    type === "review" ||
    String(notif?.title ?? "").toLowerCase().includes("review")
  );
}

export function isPaymentReleasedNotification(notif) {
  const title = String(notif?.title ?? "").toLowerCase();
  const body = String(notif?.body ?? notif?.message ?? "").toLowerCase();
  const kind = String(notif?.metadata?.kind ?? "").toLowerCase();
  return (
    kind === "payment_released" ||
    kind === "payment_released_client" ||
    title.includes("payment has been released") ||
    title.includes("payment released") ||
    body.includes("milestone amount:") ||
    body.includes("amount received:") ||
    body.includes("amount released:")
  );
}

function parseAedNumber(value) {
  const s = String(value ?? "").trim();
  if (!s) return null;
  const n = Number(s.replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) && n > 0 ? n : null;
}

function parsePaymentReleasedBody(body) {
  const text = String(body ?? "");
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  const pick = (prefix) => {
    const line = lines.find((l) => l.toLowerCase().startsWith(prefix));
    if (!line) return null;
    const after = line.split(":").slice(1).join(":").trim();
    return after || null;
  };
  return {
    gross: pick("milestone amount"),
    fee:
      pick(`liber service fee (${getPlatformFeePercent()}%)`) ||
      pick("liber service fee (10%)") ||
      pick("liber service fee (15%)") ||
      (() => {
        const line = lines.find((l) =>
          l.toLowerCase().startsWith("liber service fee"),
        );
        if (!line) return null;
        return line.split(":").slice(1).join(":").trim() || null;
      })(),
    net: pick("amount received"),
    released: pick("amount released"),
  };
}

function metaAmount(meta, key, bodyParsed, bodyKey) {
  const fromMeta = Number(meta?.[key]);
  if (Number.isFinite(fromMeta) && fromMeta > 0) return fromMeta;
  const fromBody = parseAedNumber(bodyParsed[bodyKey]);
  return fromBody != null ? fromBody : 0;
}

function fmtCurrency(currency, amount) {
  const c = String(currency || "AED").trim() || "AED";
  return `${c} ${Number(amount ?? 0).toFixed(2)}`;
}

/** Returns { audience, subtitle, rows, ctaLabel } for payment-released notifications. */
export function getPaymentReleasedDisplay(notif) {
  const meta = notif?.metadata && typeof notif.metadata === "object" ? notif.metadata : null;
  const kind = String(meta?.kind || "").toLowerCase();
  const bodyParsed = parsePaymentReleasedBody(notif?.body || notif?.message);

  if (kind === "payment_released_client") {
    const amount =
      meta?.amount_released != null
        ? fmtCurrency(meta.currency, meta.amount_released)
        : bodyParsed.released;
    return {
      audience: "client",
      subtitle: "Payment has been released to the freelancer.",
      rows: amount ? [["Amount released", amount]] : [],
      ctaLabel: null,
    };
  }

  if (kind === "payment_released") {
    const currency = meta?.currency || "AED";
    const gross = metaAmount(
      meta,
      "milestone_amount",
      bodyParsed,
      "gross",
    );
    const fee = metaAmount(meta, "service_fee", bodyParsed, "fee");
    const net = metaAmount(meta, "amount_received", bodyParsed, "net");
    const feeRate =
      Number.isFinite(Number(meta?.fee_rate)) && Number(meta.fee_rate) > 0
        ? Number(meta.fee_rate)
        : getPlatformFeeRate();
    const feePct = Math.round(feeRate * 10000) / 100;
    const displayFee =
      fee > 0 ? fee : gross > 0 ? Math.round(gross * feeRate * 100) / 100 : 0;
    const displayNet =
      net > 0 ? net : gross > 0 ? Math.round((gross - displayFee) * 100) / 100 : 0;
    return {
      audience: "freelancer",
      subtitle: "Your milestone payment is now available in earnings.",
      rows: [
        ["Gross Amount", fmtCurrency(currency, gross)],
        [
          `Platform Fee (${feePct}%)`,
          `${displayFee >= 0 ? "- " : ""}${fmtCurrency(currency, Math.abs(displayFee))}`,
        ],
        ["Net Amount Received", fmtCurrency(currency, displayNet)],
      ],
      ctaLabel: "View Earnings",
    };
  }

  if (bodyParsed.released && !bodyParsed.gross) {
    return {
      audience: "client",
      subtitle: "Payment has been released to the freelancer.",
      rows: [["Amount released", bodyParsed.released]],
      ctaLabel: null,
    };
  }

  const rows = [
    ["Gross Amount", bodyParsed.gross],
    [`Platform Fee (${getPlatformFeePercent()}%)`, bodyParsed.fee],
    ["Net Amount Received", bodyParsed.net],
  ].filter(([, v]) => v);

  return {
    audience: "freelancer",
    subtitle: "Your milestone payment is now available in earnings.",
    rows,
    ctaLabel: rows.length ? "View Earnings" : null,
  };
}
